import { useState, useEffect, useCallback, useMemo } from "react";
import { LEAGUE_IDS, type LeagueKey } from "@/lib/apiSports";

export interface FavoriteTeam {
  id: string;
  name: string;
  abbr: string;
  league: string;
  logo?: string;
}

export interface FavoritePlayer {
  id: number;
  name: string;
  team: string;
  teamLogo?: string;
  league: string;
  position: string;
  number?: number;
  photo?: string;
  nationality?: string;
}

export interface UserFavorites {
  leagues: string[];
  teams: FavoriteTeam[];
  players: FavoritePlayer[];
  alertsEnabled: boolean;
  playerAlertsEnabled: boolean;
}

// Player limits by subscription tier
export const PLAYER_LIMITS = {
  elite: 24,        // Elite gets full access
  pro: 8,           // Pro gets 1/3 of Elite (8 players)
  light: 8,         // Light gets 1/3 of Elite (8 players)
  free: 3,          // Free users get limited access
} as const;

const STORAGE_KEY = "icehub_user_favorites";

const DEFAULT_FAVORITES: UserFavorites = {
  leagues: [],
  teams: [],
  players: [
    {
      id: 10526,
      name: "Marcus Johansson",
      team: "MIN",
      league: "NHL",
      position: "LW",
      number: 90,
      nationality: "SWE",
      photo: "https://assets.nhle.com/mugs/nhl/20252026/8475149.png",
    },
    {
      id: 10536,
      name: "Jonas Brodin",
      team: "MIN",
      league: "NHL",
      position: "D",
      number: 25,
      nationality: "SWE",
      photo: "https://assets.nhle.com/mugs/nhl/20252026/8476463.png",
    },
  ],
  alertsEnabled: false,
  playerAlertsEnabled: false,
};

export function useUserFavorites() {
  const [favorites, setFavorites] = useState<UserFavorites>(DEFAULT_FAVORITES);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load favorites from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as UserFavorites;
        // Ensure players array exists for backward compatibility
        if (!parsed.players) {
          parsed.players = [];
        }
        if (parsed.playerAlertsEnabled === undefined) {
          parsed.playerAlertsEnabled = false;
        }
        setFavorites(parsed);
      }
    } catch (e) {
      console.error("Error loading favorites:", e);
    }
    setIsLoaded(true);
  }, []);

  // Save to localStorage whenever favorites change
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    }
  }, [favorites, isLoaded]);

  const addLeague = useCallback((league: string) => {
    setFavorites((prev) => {
      if (prev.leagues.includes(league)) return prev;
      return { ...prev, leagues: [...prev.leagues, league] };
    });
  }, []);

  const removeLeague = useCallback((league: string) => {
    setFavorites((prev) => ({
      ...prev,
      leagues: prev.leagues.filter((l) => l !== league),
      // Also remove teams from this league
      teams: prev.teams.filter((t) => t.league !== league),
    }));
  }, []);

  const addTeam = useCallback((team: FavoriteTeam) => {
    setFavorites((prev) => {
      if (prev.teams.some((t) => t.id === team.id && t.league === team.league)) return prev;
      return { ...prev, teams: [...prev.teams, team] };
    });
  }, []);

  const removeTeam = useCallback((teamId: string, league: string) => {
    setFavorites((prev) => ({
      ...prev,
      teams: prev.teams.filter((t) => !(t.id === teamId && t.league === league)),
    }));
  }, []);

  // Player management
  const addPlayer = useCallback((player: FavoritePlayer) => {
    setFavorites((prev) => {
      if (prev.players.some((p) => p.id === player.id && p.league === player.league)) return prev;
      return { ...prev, players: [...prev.players, player] };
    });
  }, []);

  const removePlayer = useCallback((playerId: number, league: string) => {
    setFavorites((prev) => ({
      ...prev,
      players: prev.players.filter((p) => !(p.id === playerId && p.league === league)),
    }));
  }, []);

  const toggleAlerts = useCallback((enabled: boolean) => {
    setFavorites((prev) => ({ ...prev, alertsEnabled: enabled }));
  }, []);

  const togglePlayerAlerts = useCallback((enabled: boolean) => {
    setFavorites((prev) => ({ ...prev, playerAlertsEnabled: enabled }));
  }, []);

  const isFavoriteTeam = useCallback(
    (teamId: string, league: string) => {
      return favorites.teams.some((t) => t.id === teamId && t.league === league);
    },
    [favorites.teams]
  );

  const isFavoriteLeague = useCallback(
    (league: string) => {
      return favorites.leagues.includes(league);
    },
    [favorites.leagues]
  );

  const isFavoritePlayer = useCallback(
    (playerId: number, league: string) => {
      return favorites.players.some((p) => p.id === playerId && p.league === league);
    },
    [favorites.players]
  );

  const clearAll = useCallback(() => {
    setFavorites(DEFAULT_FAVORITES);
  }, []);

  // Get favorite team IDs for quick lookups
  const favoriteTeamIds = useMemo(() => {
    return new Set(favorites.teams.map((t) => `${t.league}:${t.abbr}`));
  }, [favorites.teams]);

  const favoritePlayerIds = useMemo(() => {
    return new Set(favorites.players.map((p) => `${p.league}:${p.id}`));
  }, [favorites.players]);

  const hasFavorites = useMemo(() => {
    return favorites.leagues.length > 0 || favorites.teams.length > 0;
  }, [favorites.leagues.length, favorites.teams.length]);

  const hasFavoritePlayers = useMemo(() => {
    return favorites.players.length > 0;
  }, [favorites.players.length]);

  // Helper to get player limit based on subscription
  const getPlayerLimit = useCallback((subscription: {
    subscribed: boolean;
    tier: "light" | "pro" | "elite" | null;
    isTrial: boolean;
    isFullAccessPeriod: boolean;
  }) => {
    // Not subscribed = free tier
    if (!subscription.subscribed) {
      return PLAYER_LIMITS.free;
    }
    
    // During trial period (30 days), users get full Elite access
    if (subscription.isTrial) {
      return PLAYER_LIMITS.elite;
    }
    
    // After trial, return tier-specific limit
    switch (subscription.tier) {
      case "elite":
        return PLAYER_LIMITS.elite;
      case "pro":
        return PLAYER_LIMITS.pro;
      case "light":
        return PLAYER_LIMITS.light;
      default:
        return PLAYER_LIMITS.free;
    }
  }, []);

  const isAtPlayerLimit = useCallback((limit: number) => {
    return favorites.players.length >= limit;
  }, [favorites.players.length]);

  return {
    favorites,
    isLoaded,
    hasFavorites,
    hasFavoritePlayers,
    addLeague,
    removeLeague,
    addTeam,
    removeTeam,
    addPlayer,
    removePlayer,
    toggleAlerts,
    togglePlayerAlerts,
    isFavoriteTeam,
    isFavoriteLeague,
    isFavoritePlayer,
    clearAll,
    favoriteTeamIds,
    favoritePlayerIds,
    getPlayerLimit,
    isAtPlayerLimit,
  };
}

// Available leagues for selection
export const AVAILABLE_LEAGUES = [
  { id: "NHL", name: "NHL", country: "USA/Canada", flag: "🇺🇸" },
  { id: "AHL", name: "AHL", country: "USA/Canada", flag: "🇺🇸" },
  { id: "ECHL", name: "ECHL", country: "USA", flag: "🇺🇸" },
  { id: "KHL", name: "KHL", country: "Russia", flag: "🇷🇺" },
  { id: "SHL", name: "SHL", country: "Sweden", flag: "🇸🇪" },
  { id: "Allsvenskan", name: "HockeyAllsvenskan", country: "Sweden", flag: "🇸🇪" },
  { id: "Liiga", name: "Liiga", country: "Finland", flag: "🇫🇮" },
  { id: "Extraliga", name: "Extraliga", country: "Czech Republic", flag: "🇨🇿" },
  { id: "DEL", name: "DEL", country: "Germany", flag: "🇩🇪" },
  { id: "NL", name: "National League", country: "Switzerland", flag: "🇨🇭" },
] as const;
